'use client';

/**
 * PurchaseFlowWrapper
 *
 * A wrapper that switches between the new PurchaseFlow and legacy PurchaseDetails
 * based on the `user.wallet26` flag from Redux.
 *
 * Connected to Redux — reads purchaseDetails, user, application, and chat state
 * internally so that parent components pass only mode, callbacks, and display options.
 *
 * Usage (parent is kept minimal):
 * ```tsx
 * <PurchaseFlowWrapper
 *   mode="additionalFunds"
 *   onComplete={purchaseSuccess}
 *   onCancel={purchaseOnBack}
 *   inModal
 *   chatType={Constants.chatType.text}
 * />
 * ```
 */

import React, { useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Popover } from '@barges/react';
import { getFollowupParam } from '@app/components/purchases/purchases_utils';
import type {
  SupportedCurrency, PurchaseFlowWrapperProps, ConversationType, CloseReason
} from '../types';
import PurchaseFlow from '../PurchaseFlow';
import { getSuccessButtonTextKey } from '../utils/successButtonKey';
import { useAffiliatePromoSlots } from '../hooks';
import PurchaseDetails from '../../chat_overlay/purchase_details';
import {
  withCaptcha, getAppTheme, isKA, isPS
} from '../../../../common/config/utils';
import { purchaseFlowOnLog } from '../../../../common/config/purchaseFlowRenderHelpers';
import { getCurrentLocalization } from '../../../config/i18n';
import { getDeviceId } from '../../../config/device_id';
import { appRoutes } from '../../../config/app_routes';
import { getUser } from '../../../actions/user';
import { toPurchaseFlowTheme } from '../types';

// Re-export props type for external use
export type { PurchaseFlowWrapperProps } from '../types';

// ============================================================================
// Constants
// ============================================================================

const DEFAULT_MIN_PURCHASE_AMOUNT = 10;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Map chatType constant to analytics purchase screen name
 */
const getPurchaseScreenName = (chatType?: string): string | null => {
  if (!chatType) return null;
  if (chatType.includes('text')) return 'live chat';
  if (chatType.includes('voice')) return 'voice call';
  if (chatType.includes('video')) return 'video call';
  return null;
};

/**
 * Derive click source for the default purchase mode.
 * Mirrors getPurchaseClickSource in purchases_utils.js:
 *   - ADVISOR_BONUS type → 'bonus drawer'
 *   - Explicit Redux clickSource (e.g. 'payment methods') → use as-is
 *   - Otherwise → 'add funds' (covers SIDE_MENU and other cases)
 */
const getPurchaseClickSource = (purchaseType: string, reduxClickSource?: string): string => {
  if (purchaseType === 'advisor_bonus') return 'bonus drawer';
  if (purchaseType === 'side_menu') return 'add funds';
  return reduxClickSource || 'add funds';
};

/**
 * Derive click source for the additionalFunds (chat top-up) mode.
 * Mirrors getStartChatClickSource in utils.js:
 *   - Text chat → chatState.startChatClickSource
 *   - Voice/video → voipChat.startChatClickSource
 *   - Neither set → 'direct' (matches old location.state fallback)
 */
const getAdditionalFundsClickSource = (
  chatClickSource?: string | null,
  voipClickSource?: string | null
): string => chatClickSource ?? voipClickSource ?? 'direct';

// ============================================================================
// Component
// ============================================================================

export const PurchaseFlowWrapper: React.FC<PurchaseFlowWrapperProps> = ({
  mode = 'purchase',
  onComplete,
  onCancel,
  onDismiss,
  onAnalyticsEvent,
  renderIcon,
  renderText,
  inModal = false,
  popoverContentClassName,
  popoverClassName,
  popoverClosable = false,
  // Legacy pass-through props
  chatType,
  isContinue,
  backArrowAction,
  needUpdatePricingOptions
}) => {
  // Note: onFailed is intentionally not accepted — no callers provide it.
  // PurchaseFlow handles errors internally (retry, spending limits, dismiss).
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // ==========================================================================
  // Redux State
  // ==========================================================================

  const purchaseDetails = useSelector((state: any) => state.purchaseDetails);
  const user = useSelector((state: any) => state.user?.user);
  const anonymousUserBonusInfos = useSelector(
    (state: any) => state.application?.anonymousUser?.bonusInfos
  );
  const chatState = useSelector((state: any) => state.chat);
  const voipChat = useSelector((state: any) => state.voipChat);

  const fxRate: number = useSelector((state: any) => state.application?.dynamicConfig?.fxRate) ?? 1;
  const supportedCurrencies: SupportedCurrency[] = useSelector((state: any) => state.application?.dynamicConfig?.supportedCurrencies) ?? [];

  // ==========================================================================
  // Derived Data (from Redux)
  // ==========================================================================

  const creditsRequested = purchaseDetails?.amount || purchaseDetails?.price || 0;
  const price = purchaseDetails?.price || 0;
  const bonus = purchaseDetails?.bonus || 0;
  const isPayg = purchaseDetails?.isPayg || false;
  const currencyKey: string = user?.currency ?? 'USD';

  const duration = purchaseDetails?.duration;
  const expiresAt = purchaseDetails?.expiresAt;
  const userBonusInfos = user?.bonusInfos;
  const minPurchaseAmount = user?.minCreditRequestedCents
    ? user.minCreditRequestedCents / 100
    : DEFAULT_MIN_PURCHASE_AMOUNT;
  const clientAvailableCredit = parseFloat(user?.clientAvailableCredit) || 0;

  // Read purchaseType from the store (set by the action that opened the purchase flow)
  // and lowercase it to match the API's expected format (same as old flow's getPurchaseURL).
  const purchaseType = purchaseDetails?.purchaseType?.toLowerCase() || 'side_menu';

  // ==========================================================================
  // Affiliate Promo Slots (bannerSlot & promoFooterSlot)
  // ==========================================================================

  const handleCancel = useCallback((reason?: CloseReason) => {
    if (reason === 'user_dismissed') {
      onDismiss?.();
    } else {
      onCancel?.();
    }
  }, [onCancel, onDismiss]);

  const handleMaybeLater = useCallback(
    () => handleCancel('user_dismissed'),
    [handleCancel]
  );

  const { bannerSlot, promoFooterSlot } = useAffiliatePromoSlots({
    isAffiliatePromo: mode === 'affiliatePromo',
    userBonusInfos,
    anonymousUserBonusInfos,
    userId: user?.id,
    userXfmProgram: user?.xfmProgram,
    expiresAt,
    onMaybeLater: handleMaybeLater,
    renderText,
    locale: getCurrentLocalization()
  });

  // ==========================================================================
  // Analytics & Metadata (built from Redux state per mode)
  // ==========================================================================

  const analyticsConfig = useMemo(() => {
    switch (mode) {
      case 'additionalFunds':
        return {
          advisorId: chatState?.advisorId,
          topupFlow: purchaseDetails?.topupFlow,
          continuedSession: chatState?.continuedSession,
          flow: purchaseDetails?.flow,
          purchasesCount: user?.purchasesCount
        };
      case 'affiliatePromo':
        return {
          purchasesCount: user?.purchasesCount
        };
      case 'purchase':
      default:
        return {
          purchasesCount: user?.purchasesCount,
          flow: purchaseDetails?.flow
        };
    }
  }, [mode, chatType, chatState, purchaseDetails, user]);

  const refreshUser = useCallback(() => {
    (dispatch as any)(getUser());
  }, [dispatch]);

  const handleSpendingLimitAction = useCallback(() => {
    window.onbeforeunload = null;
    navigate(t(appRoutes.spendLimits), { state: { clickSource: 'popup' } });
    window.location.reload();
  }, [navigate, t]);

  const requestHeaders = useCallback(async () => {
    const deviceId = await getDeviceId();
    const getWebClient = () => {
      if (isKA()) return 'KAWeb';
      if (isPS()) return 'PSWeb';
      return 'PGWeb';
    };
    return {
      'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      ...(deviceId ? { WEBDEVICE_ID: deviceId } : {}),
      'Language-Code': getCurrentLocalization() || '',
      WEB_CLIENT: getWebClient()
    };
  }, []);

  const purchaseFlowConfig = useMemo(() => ({
    renderIcon,
    renderText,
    locale: getCurrentLocalization(),
    theme: toPurchaseFlowTheme(getAppTheme()),
    onLog: purchaseFlowOnLog,
    analytics: analyticsConfig,
    onAnalyticsEvent,
    withCaptcha,
    requestHeaders,
    minPurchaseAmount,
    clientAvailableCredit
  }), [renderIcon, renderText, analyticsConfig, onAnalyticsEvent, requestHeaders, minPurchaseAmount, clientAvailableCredit]);

  const metadata = useMemo(() => {
    if (mode === 'additionalFunds') {
      const getConversationType = (): ConversationType | undefined => {
        if (!chatType) return undefined;
        if (chatType.includes('voice')) return 'voice';
        if (chatType.includes('video')) return 'video';
        return 'chat';
      };
      const convType = getConversationType();
      const chargedVal = chatState?.initialCharged ?? voipChat?.initialVoipCharged;
      const durationVal = chatState?.initialDuration ?? voipChat?.initialVoipDuration;
      const getPropPrefix = (type: ConversationType | undefined): string => {
        if (type === 'voice') return 'voice';
        if (type === 'video') return 'vc';
        return 'chat';
      };
      const propPrefix = getPropPrefix(convType);
      const addFundsInitialProps: Record<string, number | undefined> = {
        [`${ propPrefix } minutes initial`]: chargedVal,
        [`${ propPrefix } charged initial`]: durationVal
      };
      return {
        advisorId: chatState?.advisorId,
        conversationType: convType,
        topupFlow: purchaseDetails?.topupFlow,
        clickSource: getAdditionalFundsClickSource(chatState?.startChatClickSource, voipChat?.startChatClickSource),
        purchaseScreen: getPurchaseScreenName(chatType),
        duration: duration ? parseFloat(duration) : undefined,
        context: getFollowupParam({ type: purchaseDetails?.purchaseType, isContinue, topupFlow: purchaseDetails?.topupFlow }).context,
        addFundsInitialProps
      };
    }
    if (mode === 'affiliatePromo') {
      return { clickSource: 'affiliate promo', purchaseScreen: 'affiliate promo' };
    }
    return {
      clickSource: getPurchaseClickSource(purchaseType, purchaseDetails?.clickSource),
      purchaseScreen: 'credit screen',
      context: 'credit_screen'
    };
  }, [mode, chatState, voipChat, purchaseDetails, chatType, duration, isContinue]);

  const successButtonTextKey = useMemo(
    () => getSuccessButtonTextKey({
      mode,
      isContinue: mode === 'additionalFunds' ? isContinue : undefined,
      conversationType: metadata?.conversationType
    }),
    [mode, isContinue, metadata?.conversationType]
  );

  // ==========================================================================
  // Render
  // ==========================================================================

  if (user?.wallet26) {
    return (
      <PurchaseFlow
        creditsRequested={ creditsRequested }
        price={ price }
        bonus={ bonus }
        fxRate={ fxRate }
        currencyKey={ currencyKey }
        supportedCurrencies={ supportedCurrencies }
        purchaseType={ purchaseType }
        isPayg={ isPayg }
        bannerSlot={ bannerSlot }
        promoFooterSlot={ promoFooterSlot }
        metadata={ metadata }
        config={ purchaseFlowConfig }
        onComplete={ onComplete }
        onCancel={ handleCancel }
        onPurchaseSuccess={ refreshUser }
        onSpendingLimitAction={ handleSpendingLimitAction }
        displayMode={ inModal ? 'modal' : 'inline' }
        mode={ mode }
        successButtonTextKey={ successButtonTextKey }
      />
    );
  }

  // Legacy PurchaseDetails — wrap in Popover if inModal
  const legacyContent = (
    <PurchaseDetails
      key="PurchaseFlowWrapperLegacy"
      chatType={ chatType }
      isContinue={ isContinue }
      hidePaymentDetails={ onCancel }
      backArrowAction={ backArrowAction }
      needUpdatePricingOptions={ needUpdatePricingOptions }
    />
  );

  if (inModal) {
    return (
      <Popover
        isOpen
        className={ popoverClassName }
        contentClassName={ popoverContentClassName }
        closable={ popoverClosable }
        onCloseButtonClick={ popoverClosable ? onCancel : undefined }
      >
        {legacyContent}
      </Popover>
    );
  }

  return legacyContent;
};

export default PurchaseFlowWrapper;
